import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import RibbonMenu from './RibbonMenu'


// Drop-down filter for the Grids IFC overlay: lists the model's
// IfcBuildingStoreys with checkboxes; checked storeys are the only
// ones whose IfcGrids render. Empty selection = all grids visible.
// Popover de filtro do marcador de origem: escolhe que categorias de
// origem aparecem no 3D (os pisos/espaços empilham etiquetas na mesma
// vertical, por isso vêm desligados por defeito).
export function OriginKindsFilterButton({ enabled }: { enabled: boolean }) {
  const [open, setOpen] = useState(false)
  const kinds = useAppStore((s) => s.originMarkerKinds)
  const setKind = useAppStore((s) => s.setOriginMarkerKind)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  // O fecho por clique fora e por Escape passou para o RibbonMenu. Tinha
  // de sair daqui: o menu vive agora num portal, fora deste invólucro,
  // por isso este tratador via um clique DENTRO do menu como um clique
  // fora e fechava-o a meio da escolha.
  if (!enabled) return null
  const entries: Array<{
    key: keyof typeof kinds
    label: string
    hint?: string
  }> = [
    { key: 'file', label: 'Origem do ficheiro IFC' },
    { key: 'site', label: 'IfcSite', hint: 'parser' },
    { key: 'building', label: 'IfcBuilding', hint: 'parser' },
    { key: 'storey', label: 'IfcBuildingStorey', hint: 'parser' },
    { key: 'space', label: 'IfcSpace', hint: 'parser' },
    {
      key: 'world',
      label: 'Origem da cena (0,0,0)',
      hint: 'debug',
    },
  ]
  const activeCount = entries.filter((e) => kinds[e.key]).length
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Escolher que origens aparecem no 3D. As categorias com 'parser' precisam do parser IFC paralelo carregado (Info do modelo > Origens da hierarquia > Calcular)."
        className={
          'rounded border px-1.5 py-0.5 text-[10px] ' +
          (activeCount > 0
            ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
            : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]')
        }
      >
        {activeCount} origens ▾
      </button>
      {open && (
        <RibbonMenu
          open={open}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={224}
          ariaLabel="Origens a mostrar"
        >
          <div className="mb-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            Origens visíveis
          </div>
          <ul className="flex flex-col gap-px">
            {entries.map((e) => (
              <li key={e.key}>
                <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 hover:bg-[var(--color-bg)]">
                  <input
                    type="checkbox"
                    checked={kinds[e.key]}
                    onChange={(ev) => setKind(e.key, ev.target.checked)}
                  />
                  <span className="text-[var(--color-text)]">{e.label}</span>
                  {e.hint && (
                    <span className="ml-auto font-mono text-[9px] text-[var(--color-text-dim)]">
                      {e.hint}
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-1.5 border-t border-[var(--color-border)] pt-1.5">
            <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 hover:bg-[var(--color-bg)]">
              <input
                type="checkbox"
                checked={kinds.labels}
                onChange={(ev) => setKind('labels', ev.target.checked)}
              />
              <span className="text-[var(--color-text)]">Etiquetas</span>
              <span className="ml-auto text-[9px] text-[var(--color-text-dim)]">
                texto junto aos tripés
              </span>
            </label>
          </div>
        </RibbonMenu>
      )}
    </div>
  )
}

export function GridsStoreyFilterButton({ enabled }: { enabled: boolean }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const filter = useAppStore((s) => s.gridsStoreyFilter)
  const setFilter = useAppStore((s) => s.setGridsStoreyFilter)
  const toggle = useAppStore((s) => s.toggleGridStorey)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const [storeys, setStoreys] = useState<
    Array<{ expressID: number; name: string }>
  >([])
  useEffect(() => {
    if (!open || !activeModelId) return
    const ids = (
      window as unknown as { __vn2rRawIfc?: unknown }
    ).__vn2rRawIfc
      ? []
      : []
    void ids
    // Use rawIfc directly without dynamic import, already in bundle.
    import('@viewer/lib/rawIfc').then(({ rawIfc }) => {
      const sIds = rawIfc.getLineIDsByType(activeModelId, 'IFCBUILDINGSTOREY')
      const out = sIds.map((id) => {
        const a = rawIfc.getAttributes(activeModelId, id)
        return { expressID: id, name: a?.name ?? `Storey #${id}` }
      })
      setStoreys(out)
    })
  }, [open, activeModelId])
  if (!enabled) return null
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Escolher quais IfcBuildingStoreys mostram grids (vazio = todos)"
        className={
          'rounded border px-1.5 py-0.5 text-[10px] ' +
          (filter.length > 0
            ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
            : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]')
        }
      >
        {filter.length === 0 ? 'todos os pisos' : `${filter.length} pisos ▾`}
      </button>
      {open && (
        <RibbonMenu
          open={open}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={224}
          maxHeight={320}
          ariaLabel="Pisos com grelhas"
        >
          <div className="mb-1 flex items-center justify-between text-[10px] text-[var(--color-text-muted)]">
            <span>Filtro de pisos</span>
            <button
              type="button"
              onClick={() => setFilter([])}
              className="rounded px-1 hover:text-[var(--color-text)]"
            >
              limpar
            </button>
          </div>
          {storeys.length === 0 ? (
            <div className="italic text-[var(--color-text-muted)]">
              Sem IfcBuildingStorey neste modelo.
            </div>
          ) : (
            <ul className="flex flex-col gap-px">
              {storeys.map((s) => {
                const checked = filter.includes(s.expressID)
                return (
                  <li key={s.expressID}>
                    <label className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 hover:bg-[var(--color-bg)]">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(s.expressID)}
                      />
                      <span className="truncate text-[var(--color-text)]">
                        {s.name}
                      </span>
                      <span className="ml-auto font-mono text-[9px] text-[var(--color-text-dim)]">
                        #{s.expressID}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </RibbonMenu>
      )}
    </div>
  )
}
